import { Controller } from "@hotwired/stimulus"
import { postForStream } from "../../lib/json_request"
import { errorKind } from "../../lib/config_tool_step"

// Stationslauf: führt die Schritte der Anleitung nacheinander aus. Je Schritt ein Panel (target "panel"); hat der
// Schritt etwas zu tun, trägt das Panel den Stimulus-Controller seines Typs (data-step-controller) mit
// `perform(run)` (Promise, wirft bei Fehler) und optional `reset()`. Ohne Controller (Anweisung) bestätigt Start nur
// den Schritt. Run-Kontext: `board` (von prepare_chip: UID, Prüfwert, Schlüssel gespeichert; serial_test ergänzt die
// DevEUI), angezeigt im target "board"; "Nächstes Gerät" leert ihn.
// Daten am Panel: data-start-label, data-auto-start, data-auto-proceed, data-invalid (Schritt kann nicht laufen,
// der Lauf hält dort an). Ohne Bridge lässt sich nichts starten (eine zu alte Shell weist schon der Server ab, dmct.md #31).
// Produktionsprotokoll (dmct.md #27): jeder Schrittversuch schickt einen Eintrag (bei Fehlern Fehlerart und Meldung, letzte
// Ausgabezeilen des Schritts, Chip-UID); der Server ordnet ihn über die UID dem Protokoll der Platine zu. Kein Starten
// oder Beenden, ein Neuladen verliert also nichts. Die Aufrufe laufen nacheinander und halten den Lauf nie an; ein
// Fehler erscheint nur im target "logError".
const ICONS = {
  pending: "bi bi-circle text-body-tertiary",
  current: "bi bi-arrow-right-circle text-primary",
  running: "bi bi-hourglass-split text-primary",
  ok: "bi bi-check-circle-fill text-success",
  failed: "bi bi-x-circle-fill text-danger",
  invalid: "bi bi-exclamation-triangle-fill text-warning",
}
const AUTO_PROCEED_DELAY_MS = 1000

export default class extends Controller {
  static targets = ["item", "icon", "panel", "done", "start", "next", "unavailable", "board", "uid", "keyStored", "deveui", "skip", "logError"]
  static values = { retryLabel: String, boardsUrl: String, logEntriesUrl: String, logErrorLabel: String }

  #index = 0
  #ready = false
  #invalid = []
  #proceedTimer = null
  #board = null
  #logCalls = Promise.resolve()

  get board() {
    return this.#board
  }

  set board(board) {
    this.#board = board
    if (!this.hasBoardTarget) return
    this.boardTarget.hidden = !board
    this.uidTarget.textContent = board?.mcu_uid ?? ""
    this.keyStoredTarget.hidden = !board?.key_stored
    this.deveuiTarget.hidden = !board?.deveui
    this.deveuiTarget.textContent = board?.deveui ? `DevEUI ${board.deveui}` : ""
  }

  connect() {
    const bridge = window.configTool
    if (!bridge) this.unavailableTarget.hidden = false
    else this.#ready = true

    if (!this.hasPanelTarget) return
    this.#invalid = this.panelTargets.map((panel) => panel.dataset.invalid === "true")
    this.panelTargets.forEach((_, i) => this.#setStatus(i, this.#invalid[i] ? "invalid" : "pending"))
    // Erst nach dem Verbinden der Schritt-Controller (Kinder) starten, sonst findet auto_start sie noch nicht.
    setTimeout(() => this.#show(0))
  }

  disconnect() {
    clearTimeout(this.#proceedTimer)
  }

  async start() {
    const index = this.#index
    const panel = this.panelTargets[index]
    this.#setStatus(index, "running")
    this.startTarget.disabled = true
    this.skipTarget.hidden = true
    this.logErrorTarget.hidden = true
    const startedAt = new Date().toISOString()
    try {
      await this.#stepController(panel)?.perform(this)
      this.#recordStep(index, startedAt, {})
      this.#setStatus(index, "ok")
      this.startTarget.hidden = true
      if (panel.dataset.autoProceed === "true") {
        this.#proceedTimer = setTimeout(() => this.next(), AUTO_PROCEED_DELAY_MS)
      } else {
        this.nextTarget.hidden = false
      }
    } catch (error) {
      // Den Fehler zeigt der Schritt-Controller in seinem Panel.
      this.#recordStep(index, startedAt, { kind: errorKind(error), message: error?.message })
      this.#setStatus(index, "failed")
      this.startTarget.textContent = this.retryLabelValue
      this.startTarget.disabled = false
      this.skipTarget.hidden = false
    }
  }

  next() {
    clearTimeout(this.#proceedTimer)
    if (this.#index + 1 < this.panelTargets.length) this.#show(this.#index + 1)
    else this.#finish()
  }

  // Nächstes Gerät: alles zurück auf Anfang.
  restart() {
    this.board = null
    this.panelTargets.forEach((panel, i) => {
      this.#stepController(panel)?.reset?.()
      this.#setStatus(i, this.#invalid[i] ? "invalid" : "pending")
    })
    this.#show(0)
  }

  #show(index) {
    this.#index = index
    const panel = this.panelTargets[index]
    this.panelTargets.forEach((p, i) => (p.hidden = i !== index))
    this.itemTargets.forEach((item, i) => item.classList.toggle("list-group-item-primary", i === index))
    this.doneTarget.hidden = true
    this.nextTarget.hidden = true
    this.skipTarget.hidden = true
    this.startTarget.hidden = false
    this.startTarget.textContent = panel.dataset.startLabel
    this.startTarget.disabled = this.#invalid[index] || !this.#ready
    if (this.#invalid[index]) return

    this.#setStatus(index, "current")
    if (this.#ready && panel.dataset.autoStart === "true") {
      this.start()
    }
  }

  #finish() {
    this.panelTargets.forEach((panel) => (panel.hidden = true))
    this.itemTargets.forEach((item) => item.classList.remove("list-group-item-primary"))
    this.startTarget.hidden = true
    this.nextTarget.hidden = true
    this.doneTarget.hidden = false
  }

  // Einträge strikt nacheinander, damit die Versuchsnummern der Reihenfolge an der Station folgen. Die UID wird jetzt
  // gelesen: "Nächstes Gerät" leert die Platine, bevor ein späterer Aufruf an der Reihe wäre. Die Antwort ersetzt die
  // Übersicht des Auftrags (#run_summary, Turbo Stream).
  #recordStep(index, startedAt, entry) {
    const lines = this.#stepController(this.panelTargets[index])?.logLines ?? []
    const body = {
      number: index + 1,
      started_at: startedAt,
      mcu_uid: this.board?.mcu_uid,
      output: lines.slice(-20),
      ...entry,
    }
    this.#logCalls = this.#logCalls
      .then(() => postForStream(this.logEntriesUrlValue, body))
      .catch((error) => {
        this.logErrorTarget.textContent = `${this.logErrorLabelValue} ${error.message}`
        this.logErrorTarget.hidden = false
      })
  }

  #setStatus(index, status) {
    this.iconTargets[index].className = ICONS[status]
  }

  #stepController(panel) {
    const identifier = panel.dataset.stepController
    return identifier ? this.application.getControllerForElementAndIdentifier(panel, identifier) : null
  }
}
